/**
 * Read-only service summary labels shared between {@link ServiceDetail} (which
 * produced them originally) and {@link ServiceSettingsPanel} (DNKADM-0295), which
 * reuses them so there stays exactly one place each label is produced. Split into
 * this plain module (rather than exporting them from `ServiceDetail.tsx`) because a
 * `.tsx` file that exports both a component and plain functions breaks React Fast
 * Refresh (`react-refresh/only-export-components`).
 */
import type { V2Service } from '@/api/services';

/**
 * Read-only guest-policy summary (DNKAPI-0145): "Guests: not allowed" when guests are
 * off, else "Guests: allowed (unlimited)" for a `0` cap or "Guests: allowed (max N)".
 */
export function guestSummary(service: V2Service): string {
  if (!service.allowGuests) return 'Guests: not allowed';
  return service.maxGuests > 0
    ? `Guests: allowed (max ${service.maxGuests})`
    : 'Guests: allowed (unlimited)';
}

/**
 * Human read-only label for the payment-capture mode (DNKAPI-0271): the two known values
 * map to friendly text; an unknown future value renders verbatim (extensible enum).
 */
export function paymentModeSummary(mode: V2Service['paymentMode']): string {
  if (mode === 'charge') return 'Charge at booking';
  if (mode === 'card_on_file') return 'Card on file, no charge';
  return mode;
}

/**
 * Plain-words label for how a service picks a coach or a bay (DNKADM-0493 Words): the
 * wire enum is unchanged; only the words the service screens show. `assignmentModeLabel`
 * (`@/domain/v2Enums`) stays as it is for the other screens that use it.
 */
export function pickModeLabel(mode: V2Service['staffAssignment']): string {
  if (mode === 'MEMBER_SELECTS') return 'Members pick';
  if (mode === 'ROUND_ROBIN') return 'Dink picks';
  return 'Not used';
}

/**
 * The status words and their one-line meanings (DNKADM-0493). Drawn from the existing
 * lifecycle value (`DRAFT` / `INACTIVE` / `ACTIVE`), which already tells Draft from
 * Turned off — no new field.
 */
export const SERVICE_STATUS_WORDS: Record<V2Service['status'], { label: string; meaning: string }> = {
  DRAFT: { label: 'Draft', meaning: 'Being built. Members have never seen it.' },
  INACTIVE: { label: 'Turned off', meaning: 'Was live. Hidden from members now.' },
  ACTIVE: { label: 'Active', meaning: 'Live. Members can see and book it.' },
};

/**
 * "Who" for a list row: how the service picks a coach and a bay, from the modes the list
 * already carries ("Coach: Members pick · Bay: Dink picks"). A dimension set to Not used
 * is left out.
 */
export function whoSummary(service: Pick<V2Service, 'staffAssignment' | 'resourceAssignment'>): string {
  const parts: string[] = [];
  if (service.staffAssignment !== 'NONE') parts.push(`Coach: ${pickModeLabel(service.staffAssignment)}`);
  if (service.resourceAssignment !== 'NONE') parts.push(`Bay: ${pickModeLabel(service.resourceAssignment)}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}
